import { SparklesIcon } from '@heroicons/react/24/outline';

import HeroIcon from '@/components/HeroIcon';
import useDashboardRoute from '@/hooks/useDashboardRoute';
import ROUTES from '@/routes/routes';

import { Copy, Label, Pill, SwitchLink } from './styles';

type Props = {
  /** Which dashboard is being rendered — decides the copy and the target. */
  current: 'v1' | 'v2';
};

// GRAVY-10832 — moves an admin between the two dashboards. Rendered into the
// page header of both (via `<Page customActions>`), so the control sits in the
// same place whichever one you are on.
//
// A <Link>, not a button: this is plain navigation, so it should be openable in
// a new tab and show its target on hover.
//
// Renders nothing for client and helpdesk admins. They have no Dashboard V2 —
// it is tenant-admin only on the route AND on the API
// (`require_tenant_admin`) — so offering them the switch would send them to a
// page that bounces them to /orders. On Dashboard V2 the guard is redundant
// (only tenant admins can be there at all); on the classic dashboard, which
// every role sees, it is doing real work.
const DashboardViewSwitch = ({ current }: Props) => {
  const { isDashboardV2Admin } = useDashboardRoute();

  if (!isDashboardV2Admin) {
    return null;
  }

  const onV2 = current === 'v2';

  return (
    <Pill id="dashboard-view-switch">
      <Copy>
        <HeroIcon icon={SparklesIcon} />
        <Label>
          {onV2 ? "You're on the new UI" : "You're on the classic UI"}
        </Label>
      </Copy>
      <SwitchLink
        id="dashboard-view-switch-link"
        to={onV2 ? `/${ROUTES.dashboardV1}` : `/${ROUTES.dashboardV2}`}
      >
        {onV2 ? 'Switch back to classic' : 'Switch to new UI'}
      </SwitchLink>
    </Pill>
  );
};

export default DashboardViewSwitch;
